// Internal-only counterpart to `use-grid-preferences-sync.ts`. Intentionally
// duplicated (not extracted into a shared core) so the internal preference flow
// stays fully independent of the external/vendor flow, which is being
// centralized separately. Do not import from the external sync hook here.

import { useQueryClient } from "@tanstack/react-query";
import type {
	GridColumnReorderEvent,
	GridColumnResizeEvent,
} from "@tql/ui-components-react/kendo/grid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { BoardColumn } from "~/shared/types/quote-board-types.ts";
import type { GridPrefColumn } from "~/shared/utils/apply-grid-prefs.ts";
import { VISIBILITY_ALL } from "~/shared/utils/apply-grid-prefs.ts";

import type { UserPreferences } from "../../schemas/user-preferences-schema.ts";
import { getGridIdName } from "../../schemas/user-preferences-schema.ts";
import {
	getInternalUserPreferencesQueryKey,
	useGetInternalUserPreferences,
} from "./use-get-internal-user-preferences.ts";
import { usePutInternalUserPreferences } from "./use-put-internal-user-preferences.ts";

const DEFAULT_VISIBILITY = VISIBILITY_ALL;
const DEFAULT_WIDTH = 100;
const PUT_DEBOUNCE_MS = 500;

type UseInternalGridPreferencesSyncOptions = {
	/**
	 * Effective default column metadata for this grid. Used to seed pref
	 * entries the first time a user changes a column that has no server pref
	 * yet, so unrelated properties (width, ordinalPosition, visibility) are
	 * populated from the real defaults instead of synthetic fallbacks.
	 */
	defaultPrefs?: GridPrefColumn[];
};

export const useInternalGridPreferencesSync = (
	gridId: number,
	options: UseInternalGridPreferencesSyncOptions = {},
) => {
	const { defaultPrefs } = options;
	const queryClient = useQueryClient();
	const { data: allPrefs } = useGetInternalUserPreferences();
	const { mutateAsync: putUserPreferences } = usePutInternalUserPreferences();

	const defaultPrefsMapRef = useRef<Map<string, GridPrefColumn>>(new Map());
	useEffect(() => {
		defaultPrefsMapRef.current = new Map(
			(defaultPrefs ?? []).map((p) => [p.columnId, p]),
		);
	}, [defaultPrefs]);

	const prefsRef = useRef<Map<string, GridPrefColumn>>(new Map());
	const allPrefsRef = useRef<UserPreferences | undefined>(undefined);
	const hasInitializedFromServerRef = useRef(false);

	const [gridKey, setGridKey] = useState(0);

	useEffect(() => {
		allPrefsRef.current = allPrefs;
	}, [allPrefs]);

	useEffect(() => {
		if (hasInitializedFromServerRef.current) return;
		const serverColumns = allPrefs?.[gridId]?.columns;
		if (!serverColumns) return;
		prefsRef.current = new Map(serverColumns.map((c) => [c.columnId, c]));
		hasInitializedFromServerRef.current = true;
	}, [allPrefs, gridId]);

	const buildNextAllPrefs = useCallback(
		(gridColumns: GridPrefColumn[]): UserPreferences => {
			const existing = allPrefsRef.current?.[gridId];

			const enumName = getGridIdName(gridId);
			const existingGridId = existing?.gridId;
			const resolvedGridId =
				enumName ??
				(existingGridId && !/^\d+$/.test(existingGridId)
					? existingGridId
					: String(gridId));
			return {
				...allPrefsRef.current,
				[gridId]: {
					...existing,
					gridId: resolvedGridId,
					columns: gridColumns,
				},
			};
		},
		[gridId],
	);

	// Flush body is stored in a ref so the debounced wrapper below stays stable
	// across renders, and `syncCache` writing to the query cache synchronously
	// inside `updatePref` can't throw away the pending PUT. The `keepalive` flag
	// is forwarded to the request so page-teardown saves (unmount/pagehide)
	// survive the document being destroyed.
	const flushImplRef = useRef<(keepalive?: boolean) => Promise<void>>(
		async () => {},
	);
	useEffect(() => {
		flushImplRef.current = async (keepalive = false) => {
			const next = buildNextAllPrefs(
				[...prefsRef.current.values()].sort(
					(a, b) => a.ordinalPosition - b.ordinalPosition,
				),
			);
			try {
				await putUserPreferences({ preferences: next, keepalive });
			} catch {
				hasInitializedFromServerRef.current = false;
			}
		};
	}, [buildNextAllPrefs, putUserPreferences]);

	const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
		undefined,
	);
	const flush = useMemo(() => {
		return () => {
			if (flushTimeoutRef.current !== undefined) {
				clearTimeout(flushTimeoutRef.current);
			}
			flushTimeoutRef.current = setTimeout(() => {
				flushTimeoutRef.current = undefined;
				void flushImplRef.current(false);
			}, PUT_DEBOUNCE_MS);
		};
	}, []);

	// Immediately fire any pending debounced PUT and clear the timer. Shared by
	// the unmount cleanup and the page-hide handlers below so a user's last
	// interaction isn't dropped while waiting out the debounce. Uses `keepalive`
	// so the request still completes if the page is being torn down. No-ops when
	// nothing is pending.
	const flushNow = useCallback(() => {
		if (flushTimeoutRef.current === undefined) return;
		clearTimeout(flushTimeoutRef.current);
		flushTimeoutRef.current = undefined;
		void flushImplRef.current(true);
	}, []);

	// On unmount (e.g. SPA navigation / logout), fire any pending debounced PUT
	// immediately rather than dropping it.
	useEffect(() => {
		return () => {
			flushNow();
		};
	}, [flushNow]);

	// A full page refresh or tab close does NOT run React effect cleanup, so the
	// unmount flush above can't save the user's last change on reload. Flush when
	// the document transitions to hidden — this fires before teardown while the
	// page is still alive enough to dispatch the request (the pattern recommended
	// over `beforeunload`/`unload`) — and on `pagehide` as a bfcache/close
	// backstop.
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState === "hidden") {
				flushNow();
			}
		};
		const handlePageHide = () => {
			flushNow();
		};
		document.addEventListener("visibilitychange", handleVisibilityChange);
		globalThis.addEventListener("pagehide", handlePageHide);
		return () => {
			document.removeEventListener("visibilitychange", handleVisibilityChange);
			globalThis.removeEventListener("pagehide", handlePageHide);
		};
	}, [flushNow]);

	const syncCache = useCallback(() => {
		const next = buildNextAllPrefs(
			[...prefsRef.current.values()].sort(
				(a, b) => a.ordinalPosition - b.ordinalPosition,
			),
		);
		allPrefsRef.current = next;
		queryClient.setQueryData(getInternalUserPreferencesQueryKey(), next);
	}, [buildNextAllPrefs, queryClient]);

	const flushRef = useRef(flush);
	const syncCacheRef = useRef(syncCache);
	useEffect(() => {
		flushRef.current = flush;
		syncCacheRef.current = syncCache;
	});

	const updatePref = useCallback(
		(columnId: string, updates: Partial<GridPrefColumn>) => {
			const existing = prefsRef.current.get(columnId);
			if (existing) {
				const isUnchanged = Object.entries(updates).every(
					([key, value]) => existing[key as keyof GridPrefColumn] === value,
				);
				if (isUnchanged) return;
				prefsRef.current.set(columnId, { ...existing, ...updates });
			} else {
				// Seed unrelated fields from the effective defaults for this column
				// (when available) so a first hide/show, resize, or reorder doesn't
				// rewrite width/order/visibility with synthetic placeholders that
				// would then replay on the next render.
				const seed = defaultPrefsMapRef.current.get(columnId);
				let ordinalPosition = seed?.ordinalPosition;
				if (ordinalPosition == null) {
					let maxPosition = -1;
					for (const p of prefsRef.current.values()) {
						if (p.ordinalPosition > maxPosition) {
							maxPosition = p.ordinalPosition;
						}
					}
					ordinalPosition = maxPosition + 1;
				}
				prefsRef.current.set(columnId, {
					columnId,
					isVisible: seed?.isVisible ?? true,
					widthSize: seed?.widthSize ?? DEFAULT_WIDTH,
					ordinalPosition,
					visibility: seed?.visibility ?? DEFAULT_VISIBILITY,
					...updates,
				});
			}
			syncCacheRef.current();
			flushRef.current();
		},
		[],
	);

	const onColumnVisibilityChange = useCallback(
		(column: BoardColumn) => {
			if (!column.field || column.required === true) return;
			updatePref(column.field, { isVisible: column.show !== false });
		},
		[updatePref],
	);

	const onColumnReorder = useCallback(
		(event: GridColumnReorderEvent) => {
			const isFixed = (
				col: GridColumnReorderEvent["columns"][number] | undefined,
			) => !col?.field || col.reorderable === false;

			// Some grids pin fixed columns to the END (e.g. the Quote Board's
			// trailing requote/action column) rather than the front. Those
			// trailing fixed columns legitimately sit after every data column, so
			// they must be excluded from the "dropped before icons" scan below —
			// otherwise the guard would trip on every reorder and discard the
			// change. Walk from the end to find the trailing fixed block.
			const columns = event.columns;
			let trailingFixedStart = columns.length;
			for (let i = columns.length - 1; i >= 0; i--) {
				if (!isFixed(columns[i])) break;
				trailingFixedStart = i;
			}

			// Detect when a data column was dragged in front of a leading
			// icon/required column by walking the non-trailing portion in display
			// order: once we've seen a data column, any fixed column appearing
			// after it means the user (or a stray reorder triggered while resizing
			// the leftmost data column) placed a data column inside the icon zone.
			let seenDataColumn = false;
			let droppedBeforeIcons = false;
			for (let i = 0; i < trailingFixedStart; i++) {
				const col = columns[i];
				if (isFixed(col) && seenDataColumn) {
					droppedBeforeIcons = true;
					break;
				}
				if (!isFixed(col)) seenDataColumn = true;
			}

			if (droppedBeforeIcons) {
				syncCacheRef.current();
				setGridKey((k) => k + 1);
				return;
			}

			for (const [index, col] of columns.entries()) {
				if (isFixed(col) || !col.field) continue;
				const position = col.orderIndex ?? index;
				updatePref(col.field, { ordinalPosition: position });
			}
		},
		[updatePref],
	);

	const onColumnResize = useCallback(
		(event: GridColumnResizeEvent) => {
			if (!event.end) return;
			// Kendo deprecated `event.index` and `event.newWidth`. Prefer
			// `event.targetColumnId` for column identification (lines up with the
			// stable `id` TraxGrid assigns — the column's `field` for data
			// columns), and resolve the width from every available source so a
			// 0/undefined deprecated field can't silently drop the update.
			const targetId = event.targetColumnId;
			const col = targetId
				? event.columns.find((c) => c.id === targetId)
				: (event.columns.find((c, i) => (c.orderIndex ?? i) === event.index) ??
					event.columns[event.index]);
			if (!col?.field) return;
			const parsedColWidth =
				typeof col.width === "number"
					? col.width
					: Number.parseFloat(col.width ?? "");
			const candidates = [Number(event.newWidth), parsedColWidth];
			const resolvedWidth = candidates.find((w) => Number.isFinite(w) && w > 0);
			if (resolvedWidth === undefined) return;
			updatePref(col.field, { widthSize: resolvedWidth });
		},
		[updatePref],
	);

	const resetColumns = useCallback(() => {
		// Force the grid back to the effective defaults instead of clearing
		// prefs entirely. Seeding `prefsRef` with copies of every default
		// column ensures the persisted PUT body contains those defaults
		// (rather than an empty array, which would look like "no prefs").
		prefsRef.current = new Map(
			[...defaultPrefsMapRef.current.entries()].map(([k, v]) => [k, { ...v }]),
		);
		// Bump the grid key so Kendo remounts with the default widths/order
		// instead of carrying over the user's resized state.
		setGridKey((k) => k + 1);
		syncCacheRef.current();
		// Internal preferences ARE persisted, so cancel any pending debounced
		// PUT and fire immediately with the defaults.
		if (flushTimeoutRef.current !== undefined) {
			clearTimeout(flushTimeoutRef.current);
			flushTimeoutRef.current = undefined;
		}
		void flushImplRef.current(false);
	}, []);

	return {
		gridKey,
		onColumnVisibilityChange,
		onColumnReorder,
		onColumnResize,
		resetColumns,
	};
};
